import React from "react";

import { updateAccount } from "../../services/stockApi";
import { showToast } from "../../utils/show_toast";

const formatKrw = (n) => "₩" + Math.round(Number(n) || 0).toLocaleString();

/**
 * AccountCards — 계좌별 카드 스트립 (DAS-171 P1 E-6, DAS-175에서 인라인 별칭 편집 추가).
 *
 * summary.accounts 기반 — 계좌가 2개 이상일 때 노출되고, 클릭 시 selectedAccountId를 토글한다.
 * "전체" 카드는 include_in_total 계좌의 합계를 보여준다(요약 총 평가액과 별개로 증권만 합산 — 예수금 제외).
 *
 * 별칭 편집은 마이페이지에도 있지만(AccountKeyCard), 계좌를 보고 있는 자리에서 바로 고칠 수 있어야
 * 왕복이 없다. 저장은 마이페이지와 같은 PATCH /api/stocks/accounts/:id 를 쓰고,
 * 성공 시 onRenamed로 부모가 요약을 다시 받아 화면 전체(칩·표·차트 라벨)를 한 번에 맞춘다.
 */
class AccountCards extends React.Component {
    constructor(props) {
        super(props);
        this.state = { editingId: null, draft: "", saving: false };
    }

    startEdit = (account) => {
        this.setState({ editingId: account.account_id, draft: account.label || "", saving: false });
    };

    cancelEdit = () => this.setState({ editingId: null, draft: "", saving: false });

    save = () => {
        const { editingId, draft } = this.state;
        const label = draft.trim();
        this.setState({ saving: true });
        updateAccount(editingId, { label })
            .then(() => {
                showToast({ type: "success", message: "별칭을 저장했습니다." });
                this.setState({ editingId: null, draft: "", saving: false });
                if (this.props.onRenamed) this.props.onRenamed();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "별칭 저장에 실패했습니다.";
                showToast({ type: "error", message: msg });
                this.setState({ saving: false });
            });
    };

    onKeyDown = (event) => {
        if (event.key === "Enter") this.save();
        else if (event.key === "Escape") this.cancelEdit();
    };

    render() {
        const { accounts, selectedAccountId, onSelect } = this.props;
        const { editingId, draft, saving } = this.state;
        const list = Array.isArray(accounts) ? accounts : [];
        if (list.length < 2) return null;

        const totalValueKRW = list.reduce(
            (sum, a) => sum + (a.include_in_total ? Number(a.value_krw) || 0 : 0),
            0
        );

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                <div className="text-sm font-semibold text-content-secondary">계좌별 보기</div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    <button
                        type="button"
                        onClick={() => onSelect(null)}
                        className={`min-h-[44px] shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${
                            selectedAccountId == null
                                ? "border-brand/30 bg-brand/20"
                                : "border-surface-border bg-surface-card-alt hover:bg-surface-hover"
                        }`}
                    >
                        <div className={`text-xs font-medium ${selectedAccountId == null ? "text-brand-light" : "text-content-secondary"}`}>
                            전체
                        </div>
                        <div className="mt-0.5 font-num text-sm font-semibold text-content">{formatKrw(totalValueKRW)}</div>
                    </button>
                    {list.map((a) => {
                        const active = selectedAccountId === a.account_id;
                        const editing = editingId === a.account_id;
                        return (
                            <div
                                key={a.account_id}
                                className={`shrink-0 rounded-xl border transition-colors ${
                                    active ? "border-brand/30 bg-brand/20" : "border-surface-border bg-surface-card-alt"
                                }`}
                            >
                                {editing ? (
                                    <div className="flex items-center gap-1.5 px-3 py-2">
                                        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- 연필 클릭 직후 바로 타이핑하게 한다 */}
                                        <input
                                            autoFocus
                                            type="text"
                                            aria-label="계좌 별칭"
                                            value={draft}
                                            disabled={saving}
                                            onChange={(e) => this.setState({ draft: e.target.value })}
                                            onKeyDown={this.onKeyDown}
                                            placeholder="예: 배우자 계좌"
                                            className="min-h-[44px] w-32 rounded-lg border border-surface-border-light bg-surface-elevated px-2 text-xs text-content placeholder:text-content-muted focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={this.save}
                                            disabled={saving}
                                            className="min-h-[44px] rounded-lg border border-brand/30 bg-brand/20 px-2.5 text-xs font-semibold text-brand-light disabled:opacity-50"
                                        >
                                            {saving ? "저장 중…" : "저장"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={this.cancelEdit}
                                            disabled={saving}
                                            className="min-h-[44px] rounded-lg border border-surface-border px-2 text-xs text-content-secondary disabled:opacity-50"
                                        >
                                            취소
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-stretch">
                                        <button
                                            type="button"
                                            onClick={() => onSelect(active ? null : a.account_id)}
                                            className="min-h-[44px] rounded-l-xl px-3 py-2 text-left transition-colors hover:bg-surface-hover"
                                        >
                                            <div className={`flex items-center gap-1 text-xs font-medium ${active ? "text-brand-light" : "text-content-secondary"}`}>
                                                {a.label}
                                                {!a.include_in_total && <span className="text-content-muted">(합계 제외)</span>}
                                            </div>
                                            <div className="mt-0.5 font-num text-sm font-semibold text-content">{formatKrw(a.value_krw)}</div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => this.startEdit(a)}
                                            aria-label={`${a.label} 별칭 수정`}
                                            title="별칭 수정"
                                            className="flex min-h-[44px] w-11 items-center justify-center rounded-r-xl text-content-muted transition-colors hover:bg-surface-hover hover:text-content-secondary"
                                        >
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="h-3.5 w-3.5"
                                                aria-hidden="true"
                                            >
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default AccountCards;
